
import { useState, useEffect } from "react";
import { TextField, Button, Paper } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

// ===== Types =====
type InventoryItem = {
  _id: string;
  name: string;
  type: "part" | "accessory";
  description?: string;
  costPrice: number;
  salePrice: number;
  quantity: number;
};

type LineItem = {
  type: "service" | "part" | "labor" | "accessory" | "custom";
  name: string;
  description: string;
  quantity: number;
  costPrice: number;
  salePrice: number;
  taxRate: number;
  discount: number;
  lineTotal: number;
  profit: number;
  inventoryId?: string;
};

type TicketItem = {
  type: "service" | "part" | "labor" | "accessory" | "custom";
  name: string;
  description?: string;
  quantity?: number;
  costPrice?: number;
  salePrice?: number;
  taxRate?: number;
  discount?: number;
  inventoryId?: string;
};

type Ticket = {
  _id: string;
  ticketNumber: string;
  status?: string;
  jobType?: string;
  deviceType?: string;
  brand?: string;
  model?: string;
  serial?: string;
  imei?: string;
  priority?: "low" | "normal" | "high" | "urgent";
  dueDate?: string;
  createdAt?: string;
  faultDescription?: string;
  diagnostics?: string;
  customerNotes?: string;
  technicianNotes?: string;
  attachments?: { url: string; caption?: string }[];
  recommendedItems?: TicketItem[];
  invoiced?: boolean;
  isInvoiced?: boolean;
  invoiceId?: string;
  invoices?: any[];
  billing?: { status?: string };
};

export default function CreateInvoicePage() {
  useProtectedRoute();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [clients, setClients] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketBlocked, setTicketBlocked] = useState(false);

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [inventoryQuery, setInventoryQuery] = useState("");
  const [selectedInventory, setSelectedInventory] = useState<InventoryItem | null>(null);
  const [lastPrefillSearch, setLastPrefillSearch] = useState<string | null>(null);

  const [form, setForm] = useState<{
    clientId: string;
    ticketId?: string;
    items: LineItem[];
    notes: string;
  }>({
    clientId: "",
    ticketId: undefined,
    items: [],
    notes: "",
  });

  const [lineItem, setLineItem] = useState<
    Omit<LineItem, "lineTotal" | "profit"> & { inventoryId?: string }
  >({
    type: "service",
    name: "",
    description: "",
    quantity: 1,
    costPrice: 0,
    salePrice: 0,
    taxRate: 0,
    discount: 0,
    inventoryId: "",
  });

  // ===== Helpers =====
  const toNumber = (n: any, fallback = 0) =>
    Number.isFinite(Number(n)) ? Number(n) : fallback;

  const computeLine = (
    qty: number,
    cost: number,
    price: number,
    taxRate: number,
    discount: number
  ) => {
    const profit = (price - cost) * qty;
    const lineTotal = qty * price * (1 + taxRate / 100) - discount;
    return { profit, lineTotal };
  };

  const isTicketInvoiced = (t?: Partial<Ticket>) => {
    if (!t) return false;
    return (
      t.invoiced === true ||
      t.isInvoiced === true ||
      Boolean(t.invoiceId) ||
      (Array.isArray(t.invoices) && t.invoices.length > 0) ||
      t.billing?.status === "invoiced"
    );
  };

  useEffect(() => {
    if (lastPrefillSearch === location.search) return;

    const params = new URLSearchParams(location.search || "");
    const cleanParam = (value: string | null) => {
      if (!value) return undefined;
      const trimmed = value.trim();
      if (!trimmed || trimmed === "undefined" || trimmed === "null") return undefined;
      return trimmed;
    };

    const queryClientId = cleanParam(params.get("clientId"));
    const queryTicketId = cleanParam(params.get("ticketId"));

    if (queryClientId || queryTicketId) {
      setForm((prev) => ({
        ...prev,
        clientId: queryClientId || prev.clientId,
        ticketId: queryTicketId ?? prev.ticketId,
      }));
    }

    setLastPrefillSearch(location.search);
  }, [location.search, lastPrefillSearch]);

  // ===== Fetch clients =====
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/clients", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClients(res.data);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [token]);


  // ===== Fetch tickets for selected client (pending & not invoiced) =====
  useEffect(() => {
    if (!form.clientId) {
      setTickets([]);
      setForm((f) => ({ ...f, ticketId: undefined }));
      setSelectedTicket(null);
      setTicketBlocked(false);
      return;
    }
    (async () => {
      try {
        const query = form.ticketId
          ? `/tickets?clientId=${form.clientId}`
          : `/tickets?clientId=${form.clientId}&status=pending&invoiced=false`;

        const res = await api.get(query, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const raw: any[] = res.data || [];
        const filtered = raw.filter((t) => {
          if (!isTicketInvoiced(t)) return true;
          return form.ticketId ? String(t._id) === String(form.ticketId) : false;
        });
        setTickets(filtered);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [form.clientId, form.ticketId, token]);

  useEffect(() => {
    if (!form.ticketId) {
      setSelectedTicket(null);
      setTicketBlocked(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/tickets/${form.ticketId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const t = res.data as Ticket;
        setSelectedTicket(t);
        setTicketBlocked(isTicketInvoiced(t));
        setTickets((prev) => {
          if (prev.some((existing) => String(existing._id) === String(t._id))) {
            return prev;
          }
          return [t, ...prev];
        });

        setForm((prev) => {
          if (prev.clientId) return prev;
          const ticketClientId =
            typeof (t as any).clientId === "object"
              ? (t as any).clientId?._id
              : (t as any).clientId;
          if (!ticketClientId) return prev;
          if (prev.clientId === String(ticketClientId)) return prev;
          return { ...prev, clientId: String(ticketClientId) };
        });

      } catch (error) {
        console.error(error);
        setSelectedTicket(null);
        setTicketBlocked(false);
      }
    })();
  }, [form.ticketId, token]);

  // ===== Fetch inventory for picker =====
  useEffect(() => {
    if (lineItem.type === "part" || lineItem.type === "accessory") {
      (async () => {
        try {
          const res = await api.get("/inventory", {
            headers: { Authorization: `Bearer ${token}` },
            params: { type: lineItem.type, search: inventoryQuery },
          });
          setInventory(res.data);
        } catch (error) {
          console.error(error);
          setInventory([]);
        }
      })();
    }
  }, [lineItem.type, inventoryQuery, token]);

  // ===== When inventory item is selected update details =====
  useEffect(() => {
    if (lineItem.inventoryId) {
      const inv = inventory.find((i) => i._id === lineItem.inventoryId) || null;
      setSelectedInventory(inv);
      if (inv) {
        setLineItem((li) => ({
          ...li,
          name: inv.name,
          description: inv.description || "",
          costPrice: inv.costPrice,
          salePrice: inv.salePrice,
        }));
      }
    } else {
      setSelectedInventory(null);
    }
  }, [lineItem.inventoryId, inventory]);

  // ===== Add line item (manual or inventory) =====
  const addLineItem = () => {
    const qty = toNumber(lineItem.quantity, 1) || 1;
    const cost = toNumber(lineItem.costPrice, 0);
    const price = toNumber(lineItem.salePrice, 0);
    const taxRate = toNumber(lineItem.taxRate, 0);
    const discount = toNumber(lineItem.discount, 0);

    if (lineItem.type === "part" || lineItem.type === "accessory") {
      if (!lineItem.inventoryId) return alert("Select from inventory.");
      const inv = inventory.find((i) => i._id === lineItem.inventoryId);
      if (!inv) return alert("Selected item not found.");
      if (qty > inv.quantity) return alert("Cannot invoice more than available stock.");

      const { profit, lineTotal } = computeLine(qty, cost, price, taxRate, discount);
      const newItem: LineItem = {
        type: lineItem.type,
        name: inv.name,
        description: inv.description || "",
        quantity: qty,
        costPrice: cost,
        salePrice: price,
        taxRate,
        discount,
        lineTotal,
        profit,
        inventoryId: inv._id,
      };
      setForm((f) => ({ ...f, items: [...f.items, newItem] }));
    } else {
      const { profit, lineTotal } = computeLine(qty, cost, price, taxRate, discount);
      const newItem: LineItem = {
        ...lineItem,
        quantity: qty,
        costPrice: cost,
        salePrice: price,
        taxRate,
        discount,
        lineTotal,
        profit,
      };
      setForm((f) => ({ ...f, items: [...f.items, newItem] }));
    }

    // Reset line item
    setLineItem({
      type: "service",
      name: "",
      description: "",
      quantity: 1,
      costPrice: 0,
      salePrice: 0,
      taxRate: 0,
      discount: 0,
      inventoryId: "",
    });
    setSelectedInventory(null);
    setInventoryQuery("");
  };

  const removeLineItem = (idx: number) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  // Add a recommended ticket item into invoice items
  const addTicketItemToInvoice = (ti: TicketItem) => {
    const qty = toNumber(ti.quantity, 1) || 1;
    const cost = toNumber(ti.costPrice, 0);
    const price = toNumber(ti.salePrice, 0);
    const taxRate = toNumber(ti.taxRate, 0);
    const discount = toNumber(ti.discount, 0);
    const { profit, lineTotal } = computeLine(qty, cost, price, taxRate, discount);

    const li: LineItem = {
      type: ti.type,
      name: ti.name,
      description: ti.description || "",
      quantity: qty,
      costPrice: cost,
      salePrice: price,
      taxRate,
      discount,
      profit,
      lineTotal,
      ...(ti.inventoryId ? { inventoryId: ti.inventoryId } : {}),
    };
    setForm((f) => ({ ...f, items: [...f.items, li] }));
  };

  const addAllRecommended = () => {
    if (!selectedTicket?.recommendedItems?.length) return;
    selectedTicket.recommendedItems.forEach(addTicketItemToInvoice);
  };

  // ===== Totals =====
  const calculateTotals = () => {
    const subtotal = form.items.reduce(
      (sum, it) => sum + toNumber(it.salePrice, 0) * (toNumber(it.quantity, 1) || 1),
      0
    );
    const taxTotal = form.items.reduce(
      (sum, it) =>
        sum +
        toNumber(it.salePrice, 0) *
          (toNumber(it.quantity, 1) || 1) *
          (toNumber(it.taxRate, 0) / 100),
      0
    );
    const discountTotal = form.items.reduce((sum, it) => sum + toNumber(it.discount, 0), 0);
    const grandTotal = subtotal + taxTotal - discountTotal;
    return { subtotal, taxTotal, discountTotal, grandTotal };
  };

  // ===== Submit invoice =====
  const handleSubmit = async () => {
    if (form.items.length === 0) return alert("Add at least one line item.");
    if (!form.clientId) return alert("Select a client.");
    if (ticketBlocked) return;

    const { subtotal, taxTotal, discountTotal, grandTotal } = calculateTotals();

    const payload = {
      clientId: form.clientId,
      ticketId: form.ticketId || undefined,
      items: form.items.map((item) => ({
        ...item,
        costPrice: toNumber(item.costPrice, 0),
        sellingPrice: toNumber(item.salePrice, 0), // backend expects sellingPrice
        taxRate: toNumber(item.taxRate, 0),
        discount: toNumber(item.discount, 0),
        quantity: toNumber(item.quantity, 1),
        lineTotal: toNumber(item.lineTotal, item.salePrice * item.quantity),
      })),
      notes: form.notes,
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxTotal: parseFloat(taxTotal.toFixed(2)),
      discountTotal: parseFloat(discountTotal.toFixed(2)),
      grandTotal: parseFloat(grandTotal.toFixed(2)),
    };

    try {
      const res = await api.post("/invoices/manual", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Optional: mark ticket as invoiced if supported by your API
      if (form.ticketId) {
        try {
          await api.patch(
            `/tickets/${form.ticketId}`,
            { invoiced: true, invoiceId: res.data._id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch {
          // ignore if not supported
        }
      }

      alert("Invoice created!");
      navigate(`/invoices/${res.data._id}`);
    } catch (err: any) {
      alert(
        err.response?.data?.message ||
          "Failed to create invoice. Check stock and try again."
      );
    }
  };

  // ===== UI =====
  return (
    <div className="max-w-5xl mx-auto px-3 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-blue-800 mb-1">
          Create Invoice
        </h1>
        <p className="text-gray-500">
          Generate a new invoice, link a ticket, and add line items from inventory or manual entry.
          Ticket details show below when selected.
        </p>
      </div>

      {/* Select Client & Ticket */}
      <Paper elevation={0} className="rounded-2xl mb-6 p-6 flex flex-col md:flex-row gap-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
        <div className="flex-1">
          <label className="block font-bold mb-1">
            Client <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 font-medium"
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.target.value })}
          >
            <option value="">Select client…</option>
            {clients.map((c) => (
              <option key={c._id} value={c._id}>
                {c.fullName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block font-bold mb-1">Open Ticket (optional)</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            disabled={!form.clientId}
            value={form.ticketId}
            onChange={(e) => setForm({ ...form, ticketId: e.target.value })}
          >
            <option value="">-- no ticket --</option>
            {tickets.map((t) => (
              <option key={t._id} value={t._id}>
                #{t.ticketNumber} — {t.jobType || "-"} / {t.deviceType || "-"}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Only tickets not yet invoiced are shown.
          </p>
        </div>
      </Paper>

      {/* Ticket Details (when selected) */}
      {selectedTicket && (
      <Paper elevation={0} className="rounded-2xl mb-8 p-6" sx={{ backgroundColor: (theme)=>theme.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
          {ticketBlocked && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-700 px-4 py-2 text-sm">
              This ticket is already invoiced. You can’t create another invoice from it.
          </div>
          )}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-blue-700">
              Ticket Details — #{selectedTicket.ticketNumber}
            </h2>
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                selectedTicket.status === "pending"
                  ? "bg-yellow-100 text-yellow-800"
                  : selectedTicket.status === "in_progress"
                  ? "bg-blue-100 text-blue-800"
                  : selectedTicket.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {selectedTicket.status || "—"}
            </span>
          </div>

          {/* Device / Meta */}
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <div>
                <b>Device:</b> {selectedTicket.deviceType || "—"}
              </div>
              <div>
                <b>Brand / Model:</b> {selectedTicket.brand || "—"}
                {selectedTicket.model ? ` • ${selectedTicket.model}` : ""}
              </div>
              <div>
                <b>Identifiers:</b>{" "}
                {[
                  selectedTicket?.serial ? `Serial: ${selectedTicket.serial}` : null,
                  selectedTicket?.imei ? `IMEI: ${selectedTicket.imei}` : null,
                ]
                  .filter(Boolean)
                  .join(" • ") || "—"}
              </div>
              <div>
                <b>Job Type:</b> {selectedTicket.jobType || "—"}
              </div>
            </div>
            <div className="space-y-1">
              <div>
                <b>Priority:</b> {selectedTicket.priority || "—"}
              </div>
              <div>
                <b>Created:</b>{" "}
                {selectedTicket.createdAt
                  ? new Date(selectedTicket.createdAt).toLocaleString()
                  : "—"}
              </div>
              <div>
                <b>Due Date:</b>{" "}
                {selectedTicket.dueDate
                  ? new Date(selectedTicket.dueDate).toLocaleDateString()
                  : "—"}
              </div>
            </div>
          </div>

          {/* Problem / Notes */}
          <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
            <div className="md:col-span-1">
              <div className="font-semibold mb-1">Fault Description</div>
              <div className="p-3 rounded">
                {selectedTicket.faultDescription || "—"}
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="font-semibold mb-1">Diagnostics</div>
              <div className="p-3 rounded">
                {selectedTicket.diagnostics || "—"}
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="font-semibold mb-1">Technician Notes</div>
              <div className="p-3 rounded">
                {selectedTicket.technicianNotes || "—"}
              </div>
            </div>
          </div>

          {/* Attachments */}
          {selectedTicket.attachments?.length ? (
            <div className="mt-4">
              <div className="font-semibold mb-2">Photos / Attachments</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {selectedTicket.attachments.map((a, i) => (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block border rounded-lg overflow-hidden"
                    title={a.caption || `Attachment ${i + 1}`}
                  >
                    <img
                      src={a.url}
                      alt={a.caption || `Attachment ${i + 1}`}
                      className="w-full h-28 object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          ) : null}

          {/* Recommended Items */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">Recommended Items</h3>
              <button
                onClick={addAllRecommended}
                disabled={!selectedTicket.recommendedItems?.length}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg disabled:opacity-40"
              >
                Add All to Invoice
              </button>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-3 text-left text-gray-700">Type</th>
                    <th className="py-2 px-3 text-left text-gray-700">Name</th>
                    <th className="py-2 px-3 text-left text-gray-700">Description</th>
                    <th className="py-2 px-3 text-center text-gray-700">Qty</th>
                    <th className="py-2 px-3 text-right text-gray-700">Cost</th>
                    <th className="py-2 px-3 text-right text-gray-700">Price</th>
                    <th className="py-2 px-3 text-center text-gray-700">Tax %</th>
                    <th className="py-2 px-3 text-center text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!selectedTicket.recommendedItems?.length && (
                    <tr>
                      <td
                        className="py-3 px-3 text-center text-gray-400"
                        colSpan={8}
                      >
                        No recommended items on this ticket.
                      </td>
                    </tr>
                  )}
                  {selectedTicket.recommendedItems?.map((ti, i) => (
                <tr key={i}>
                      <td className="py-2 px-3">{ti.type}</td>
                      <td className="py-2 px-3">{ti.name}</td>
                      <td className="py-2 px-3">{ti.description || "—"}</td>
                      <td className="py-2 px-3 text-center">
                        {toNumber(ti.quantity, 1)}
                      </td>
                      <td className="py-2 px-3 text-right">
                        ${toNumber(ti.costPrice, 0).toFixed(2)}
                      </td>
                      <td className="py-2 px-3 text-right">
                        ${toNumber(ti.salePrice, 0).toFixed(2)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {toNumber(ti.taxRate, 0)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={() => addTicketItemToInvoice(ti)}
                          className="text-emerald-700 font-semibold hover:underline"
                        >
                          Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Paper>
      )}

      {/* Add Line Item */}
      <Paper elevation={0} className="rounded-2xl mb-8 p-6" sx={{ backgroundColor: (theme)=>theme.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
        <h2 className="text-xl font-semibold mb-4 text-blue-700">Add Line Item</h2>
        <div className="flex flex-wrap gap-4 mb-2">
          {/* Category */}
          <div className="flex flex-col flex-1 min-w-[160px]">
            <label className="mb-1 font-semibold">Category</label>
            <select
              className="border rounded-lg px-2 py-1"
              value={lineItem.type}
              onChange={(e) =>
                setLineItem((li) => ({
                  ...li,
                  type: e.target.value as any,
                  ...(e.target.value === "part" || e.target.value === "accessory"
                    ? {
                        name: "",
                        description: "",
                        costPrice: 0,
                        salePrice: 0,
                        inventoryId: "",
                      }
                    : { inventoryId: "" }),
                }))
              }
            >
              <option value="service">Service</option>
              <option value="part">Part</option>
              <option value="labor">Labor</option>
              <option value="accessory">Accessory</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Inventory Picker */}
          {(lineItem.type === "part" || lineItem.type === "accessory") && (
            <div className="flex-1 min-w-[260px]">
              <label className="mb-1 font-semibold">Select from Inventory</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1 mb-1"
                placeholder="Search inventory…"
                value={inventoryQuery}
                onChange={(e) => setInventoryQuery(e.target.value)}
              />
              <select
                className="w-full border rounded px-2 py-1"
                value={lineItem.inventoryId}
                onChange={(e) =>
                  setLineItem((li) => ({ ...li, inventoryId: e.target.value }))
                }
              >
                <option value="">Select item…</option>
                {inventory.map((inv) => (
                  <option key={inv._id} value={inv._id}>
                    {inv.name} | Stock: {inv.quantity} | $
                    {Number(inv.salePrice || 0).toFixed(2)}
                  </option>
                ))}
              </select>

              {selectedInventory && (
                <div className="mt-2 p-2 border rounded text-sm shadow-sm">
                  <div>
                    <b>Name:</b> {selectedInventory.name}
                  </div>
                  <div>
                    <b>Description:</b> {selectedInventory.description || "—"}
                  </div>
                  <div>
                    <b>Stock:</b> {selectedInventory.quantity}
                  </div>
                  <div>
                    <b>Cost Price:</b> $
                    {Number(selectedInventory.costPrice || 0).toFixed(2)}
                  </div>
                  <div>
                    <b>Selling Price:</b> $
                    {Number(selectedInventory.salePrice || 0).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Fields */}
          {lineItem.type !== "part" && lineItem.type !== "accessory" && (
            <>
              <div className="flex flex-col flex-1 min-w-[160px]">
                <label className="mb-1 font-semibold">Name</label>
                <input
                  type="text"
                  className="border rounded px-2 py-1"
                  value={lineItem.name}
                  onChange={(e) =>
                    setLineItem((li) => ({ ...li, name: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="mb-1 font-semibold">Description</label>
                <input
                  type="text"
                  className="border rounded px-2 py-1"
                  value={lineItem.description}
                  onChange={(e) =>
                    setLineItem((li) => ({ ...li, description: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[100px]">
                <label className="mb-1 font-semibold">Cost Price</label>
                <input
                  type="number"
                  className="border rounded px-2 py-1"
                  value={lineItem.costPrice}
                  onChange={(e) =>
                    setLineItem((li) => ({
                      ...li,
                      costPrice: toNumber(e.target.value, 0),
                    }))
                  }
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[100px]">
                <label className="mb-1 font-semibold">Selling Price</label>
                <input
                  type="number"
                  className="border rounded px-2 py-1"
                  value={lineItem.salePrice}
                  onChange={(e) =>
                    setLineItem((li) => ({
                      ...li,
                      salePrice: toNumber(e.target.value, 0),
                    }))
                  }
                />
              </div>
            </>
          )}

          {/* Always: Quantity, Tax, Discount */}
          <div className="flex flex-col flex-1 min-w-[80px]">
            <label className="mb-1 font-semibold">Quantity</label>
            <input
              type="number"
              min={1}
              max={
                lineItem.type === "part" || lineItem.type === "accessory"
                  ? selectedInventory?.quantity || 1
                  : undefined
              }
              className="border rounded px-2 py-1"
              value={lineItem.quantity}
              onChange={(e) =>
                setLineItem((li) => ({
                  ...li,
                  quantity: toNumber(e.target.value, 1) || 1,
                }))
              }
              disabled={
                (lineItem.type === "part" || lineItem.type === "accessory") &&
                !lineItem.inventoryId
              }
            />
            {(lineItem.type === "part" || lineItem.type === "accessory") &&
              lineItem.inventoryId && (
                <span className="text-xs text-gray-500">
                  Available: {selectedInventory?.quantity ?? 0}
                </span>
              )}
          </div>
          <div className="flex flex-col flex-1 min-w-[80px]">
            <label className="mb-1 font-semibold">Tax %</label>
            <input
              type="number"
              min={0}
              className="border rounded px-2 py-1"
              value={lineItem.taxRate}
              onChange={(e) =>
                setLineItem((li) => ({
                  ...li,
                  taxRate: toNumber(e.target.value, 0),
                }))
              }
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[80px]">
            <label className="mb-1 font-semibold">Discount</label>
            <input
              type="number"
              min={0}
              className="border rounded px-2 py-1"
              value={lineItem.discount}
              onChange={(e) =>
                setLineItem((li) => ({
                  ...li,
                  discount: toNumber(e.target.value, 0),
                }))
              }
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button type="button" onClick={addLineItem} variant="contained" color="success">
            + Add Item
          </Button>
        </div>
      </Paper>

      {/* Invoice Items */}
      <Paper elevation={0} className="rounded-2xl mb-8 p-0 overflow-x-auto" sx={{ backgroundColor: (theme)=>theme.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-3 text-left text-gray-700">Category</th>
              <th className="py-2 px-3 text-left text-gray-700">Name</th>
              <th className="py-2 px-3 text-center text-gray-700">Qty</th>
              <th className="py-2 px-3 text-right text-gray-700">Cost</th>
              <th className="py-2 px-3 text-right text-gray-700">Price</th>
              <th className="py-2 px-3 text-center text-gray-700">Tax %</th>
              <th className="py-2 px-3 text-right text-gray-700">Discount</th>
              <th className="py-2 px-3 text-right text-gray-700">Profit</th>
              <th className="py-2 px-3 text-right text-gray-700">Total</th>
              <th className="py-2 px-3 text-center text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {form.items.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-gray-400 py-4">
                  No items added.
                </td>
              </tr>
            )}
            {form.items.map((it, idx) => (
              <tr key={idx}>
                <td className="py-2 px-3">{it.type}</td>
                <td className="py-2 px-3">{it.name}</td>
                <td className="py-2 px-3 text-center">{it.quantity}</td>
                <td className="py-2 px-3 text-right">
                  ${Number(it.costPrice || 0).toFixed(2)}
                </td>
                <td className="py-2 px-3 text-right">
                  ${Number(it.salePrice || 0).toFixed(2)}
                </td>
                <td className="py-2 px-3 text-center">{it.taxRate || 0}%</td>
                <td className="py-2 px-3 text-right">
                  ${Number(it.discount || 0).toFixed(2)}
                </td>
                <td className="py-2 px-3 text-right">
                  ${Number(it.profit || 0).toFixed(2)}
                </td>
                <td className="py-2 px-3 text-right">
                  ${Number(it.lineTotal || 0).toFixed(2)}
                </td>
                <td className="py-2 px-3 text-center">
                  <button
                    onClick={() => removeLineItem(idx)}
                    className="text-red-600 font-semibold hover:underline"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>

      {/* Notes & Totals */}
      <Paper elevation={0} className="rounded-2xl p-6 mb-8 flex flex-col md:flex-row gap-8 items-center md:items-end" sx={{ backgroundColor: (theme)=>theme.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
        <div className="flex-1">
          <label className="block font-bold mb-1">Invoice Notes (optional)</label>
          <TextField
            label="Invoice Notes (optional)"
            multiline
            minRows={3}
            fullWidth
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <div className="flex-1 space-y-2">
          {(() => {
            const { subtotal, taxTotal, discountTotal, grandTotal } = calculateTotals();
            return (
              <>
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span className="font-medium">${taxTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount:</span>
                  <span className="font-medium">- ${discountTotal.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 flex justify-between font-bold text-lg">
                  <span>Grand Total:</span>
                  <span>${grandTotal.toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </div>
        <div className="flex flex-col items-end mt-2">
          <Button onClick={handleSubmit} disabled={ticketBlocked} variant="contained">
            Save Invoice
          </Button>
        </div>
      </Paper>
    </div>
  );
}








